import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SKILLS, type PipelineNode, type PipelineEdge, type GroupNode, type StickyNote, type GroupType, type SkillDef, type Scheduler } from '@/mocks/pipeline';
import { AGENTS, emptyChat, listedAgentId, type Agent, type Chat } from '@/mocks/chat';
import { allowLocalModels } from '@/platform';
import { nodeInGroup } from './flowUtils';
import { toPalette } from './toPalette';
import {
  listSkills, resolveSkill, createOutcome, updateOutcome, getOutcome, getProject, patchProject, runOutcome,
  getRunStatus, getArtifacts, scoreRun, reviewRun, trainOutcome,
  getLLMSettings, putLLMSettings, listLLMModels, sendChat, createSkill, getAccount,
  listProjectFiles, uploadProjectFile, deleteProjectFile,
  type Outcome, type LiveRun, type LLMSettings, type SkillDef as ApiSkill, type ProjectFile,
} from '@/api';
import { isLoggedIn } from '@/auth/token';
import { hostedCreditsMode } from './hostedCredits';
import { applyLiveRunToNodes, buildOutcomePayload, hydrateCanvasFromOutcome } from './runFlow';
import { buildComposePipeline, type ComposeRecommendation } from './composeCanvas';
import { PROMPT_NODE_ID } from './promptNode';
import { artifactsForRun, primaryArtifact, type OutputNode } from './outputFiles';
import { bestUserScore, canTrainFromScores, mergeScoreChecks, runScoreHistoryFromArtifacts, scoreTargetRunId, unscoredCompletedRunId, type ScoreCheck } from './runScorePanel';
import { packAnalyzeRuns, playbooksFromArtifacts } from './analyzeContext';
import {
  activeChatKey, loadChats, loadAnalyzeMessages, projectFromNavState, projectIdFromSearch,
  saveChats, saveAnalyzeMessages, studioTabKey, type CenterTab, parseRightTab, type RightTab,
  migrateCenterTab, rightTabForCenter, DOCKED_CHAT_KEY, parseChatDocked, chatDockedForCenter,
} from './studioPersist';
import { layoutStorageKey, layoutCanvasNodes, readCanvasLayout, writeCanvasLayout } from './canvasLayout';

function nowTime() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function useHome() {
  const loc = useLocation();
  const urlProjectId = projectIdFromSearch(loc.search);
  const bootProject = projectFromNavState(loc.state, urlProjectId);

  const [skills, setSkills] = useState<SkillDef[]>(SKILLS);
  const [skillMap, setSkillMap] = useState<Record<string, SkillDef>>(() => Object.fromEntries(SKILLS.map((s) => [s.id, s])));
  const [pipelineName, setPipelineName] = useState(() => bootProject?.title || '');
  const [description, setDescription] = useState(() => bootProject?.outcome_description || '');
  const [projectType, setProjectType] = useState(() => bootProject?.outcome_type || '');
  const [projectId, setProjectId] = useState<string | null>(urlProjectId);
  const [version] = useState('v2.1');
  const [dirty, setDirty] = useState(false);
  const [nodes, setNodes] = useState<PipelineNode[]>([]);
  const [edges, setEdges] = useState<PipelineEdge[]>([]);
  const [groups, setGroups] = useState<GroupNode[]>([]);
  const [stickies, setStickies] = useState<StickyNote[]>([]);
  const [schedulers, setSchedulers] = useState<Scheduler[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(PROMPT_NODE_ID);
  const [previewSkillId, setPreviewSkillId] = useState<string | null>(null);
  const [previewOutcome, setPreviewOutcome] = useState(false);
  const [centerTab, setCenterTabState] = useState<CenterTab>(() => {
    try {
      return migrateCenterTab(
        localStorage.getItem(studioTabKey(urlProjectId)),
        localStorage.getItem(`sh_right_tab:${urlProjectId}`),
      );
    } catch {
      return 'canvas';
    }
  });
  const [rightTab, setRightTab] = useState<RightTab>(() => {
    try {
      const center = migrateCenterTab(
        localStorage.getItem(studioTabKey(urlProjectId)),
        localStorage.getItem(`sh_right_tab:${urlProjectId}`),
      );
      return rightTabForCenter(center, parseRightTab(localStorage.getItem(`sh_right_tab:${urlProjectId}`)));
    } catch {
      return 'pipeline';
    }
  });
  const [chatDocked, setChatDockedState] = useState<boolean>(() => {
    try {
      return parseChatDocked(localStorage.getItem(DOCKED_CHAT_KEY));
    } catch {
      return true;
    }
  });
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeEdgeIds, setActiveEdgeIds] = useState<Set<string>>(new Set());
  const [lastRun, setLastRun] = useState('—');
  const [score, setScore] = useState('—/12');
  const [showExport, setShowExport] = useState(false);
  const [showFork, setShowFork] = useState(false);
  const [outcomeId, setOutcomeId] = useState<string | null>(null);
  const [outcomeSlug, setOutcomeSlug] = useState<string | null>(null);
  const [liveRun, setLiveRun] = useState<LiveRun | null>(null);
  const [artifactTree, setArtifactTree] = useState<OutputNode[]>([]);
  const [projectFiles, setProjectFiles] = useState<ProjectFile[]>([]);
  const [projectFilesBusy, setProjectFilesBusy] = useState(false);
  const [projectFilesError, setProjectFilesError] = useState<string | null>(null);
  const [scorePrompt, setScorePrompt] = useState(false);
  const [userScore, setUserScore] = useState<number | null>(null);
  const [llmScore, setLlmScore] = useState<number | null>(null);
  const [reviewMd, setReviewMd] = useState('');
  const [scoreChecks, setScoreChecks] = useState<ScoreCheck[]>([]);
  const [artifactFocusId, setArtifactFocusId] = useState<string | null>(null);
  const applyArtifactTree = useCallback((tree: OutputNode[]) => {
    setArtifactTree(tree);
    const primary = primaryArtifact(tree);
    if (primary) setArtifactFocusId(primary.id);
  }, []);
  const [elapsedMs, setElapsedMs] = useState(0);
  const pollRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);
  const outcomeIdRef = useRef(outcomeId);
  outcomeIdRef.current = outcomeId;
  const liveRunRef = useRef(liveRun);
  liveRunRef.current = liveRun;
  const artifactTreeRef = useRef(artifactTree);
  artifactTreeRef.current = artifactTree;
  const selectedNode = useMemo(() => nodes.find((n) => n.id === selectedId) || null, [nodes, selectedId]);
  const previewSkill = useMemo(() => skills.find((s) => s.id === previewSkillId) || null, [skills, previewSkillId]);
  const markDirty = () => { if (!running) setDirty(true); };

  const [chats, setChats] = useState<Chat[]>(() => {
    try {
      return loadChats(urlProjectId, localStorage);
    } catch {
      return [emptyChat()];
    }
  });
  const [activeChatId, setActiveChatId] = useState(() => {
    try {
      return localStorage.getItem(activeChatKey(urlProjectId)) || '';
    } catch {
      return '';
    }
  });
  const [typing, setTyping] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [analyzeMessages, setAnalyzeMessages] = useState<Chat['messages']>(() => {
    try {
      return loadAnalyzeMessages(urlProjectId, localStorage);
    } catch {
      return [];
    }
  });
  const [analyzeTyping, setAnalyzeTyping] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [llmSettings, setLlmSettings] = useState<LLMSettings | null>(null);
  const [models, setModels] = useState<string[]>([]);
  const [loadedModels, setLoadedModels] = useState<string[]>([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState<string | null>(null);
  const [openaiKey, setOpenaiKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [hostingActive, setHostingActive] = useState(false);

  const activeChat = useMemo(() => chats.find((c) => c.id === activeChatId) ?? chats[0] ?? null, [chats, activeChatId]);
  const chatVisible = chatDockedForCenter(centerTab, chatDocked) && !!activeChat;

  useEffect(() => {
    if (!activeChatId && chats[0]) setActiveChatId(chats[0].id);
  }, [activeChatId, chats]);

  useEffect(() => {
    try {
      saveChats(projectId, chats, localStorage);
    } catch {
      /* ignore */
    }
  }, [chats, projectId]);

  useEffect(() => {
    try {
      localStorage.setItem(studioTabKey(projectId), centerTab);
    } catch {
      /* ignore */
    }
  }, [centerTab, projectId]);

  const setCenterTab = useCallback((tab: CenterTab) => {
    setCenterTabState(tab);
    setRightTab((cur) => rightTabForCenter(tab, cur));
  }, []);

  const setChatDocked = useCallback((docked: boolean) => {
    setChatDockedState(docked);
    if (!docked) setCenterTabState((cur) => (cur === 'chat' ? 'canvas' : cur));
  }, []);

  const onSetChatModel = (model: string) => {
    updateActiveChat((c) => ({ ...c, model }));
  };

  useEffect(() => {
    try {
      localStorage.setItem(DOCKED_CHAT_KEY, chatDocked ? 'fullscreen' : 'docked');
    } catch {
      /* ignore */
    }
  }, [chatDocked]);

  useEffect(() => {
    try {
      localStorage.setItem(`sh_right_tab:${projectId}`, rightTab);
    } catch {
      /* ignore */
    }
  }, [rightTab, projectId]);

  useEffect(() => {
    if (!activeChatId) return;
    try {
      localStorage.setItem(activeChatKey(projectId), activeChatId);
    } catch {
      /* ignore */
    }
  }, [activeChatId, projectId]);

  useEffect(() => {
    try {
      saveAnalyzeMessages(projectId, analyzeMessages, localStorage);
    } catch {
      /* ignore */
    }
  }, [analyzeMessages, projectId]);

  const addSkill = useCallback((def: SkillDef) => {
    setSkills((prev) => (prev.some((s) => s.id === def.id) ? prev.map((s) => (s.id === def.id ? def : s)) : [def, ...prev]));
    setSkillMap((prev) => ({ ...prev, [def.id]: def }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const delaysMs = [0, 400, 800, 1600, 3200];
    const apply = (data: ApiSkill[]) => {
      if (!data?.length) return false;
      const norm = data.map((s) => toPalette(s));
      setSkills(norm);
      setSkillMap(Object.fromEntries(norm.map((s) => [s.id, s])));
      return true;
    };
    (async () => {
      for (let i = 0; i < delaysMs.length; i++) {
        if (delaysMs[i]) await new Promise((r) => setTimeout(r, delaysMs[i]));
        if (cancelled) return;
        try {
          if (apply(await listSkills())) return;
        } catch {
          // API often starts after Vite; retry until it listens.
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isLoggedIn()) return;
    getLLMSettings().then((s) => {
      setLlmSettings(s);
      setChats((cs) => {
        if (!cs.length) return cs;
        const first = cs[0];
        if (first.messages.length > 0) return cs;
        const agent = (['lmstudio', 'openai', 'anthropic', 'openrouter'] as Agent['id'][]).includes(s.provider as Agent['id'])
          ? (s.provider as Agent['id'])
          : first.agentId;
        return cs.map((c, i) => (i === 0 ? {
          ...c,
          agentId: agent,
          model: s.model || c.model,
          baseUrl: s.base_url || c.baseUrl,
        } : c));
      });
    }).catch(() => {});
    getAccount().then((a) => {
      setCreditBalance(a.credit_balance);
      setHostingActive(a.subscription?.status === 'active');
    }).catch(() => setCreditBalance(null));
  }, []);

  const loadOutcome = useCallback((o: Outcome) => {
    let saved = null;
    try {
      saved = readCanvasLayout(layoutStorageKey(o.id, projectId), localStorage);
    } catch {
      saved = null;
    }
    const canvas = hydrateCanvasFromOutcome(o, skillMap, saved);
    setOutcomeId(o.id);
    setOutcomeSlug(o.slug || null);
    setPipelineName(o.title || pipelineName);
    if (o.description) setDescription(o.description);
    setNodes(canvas.nodes);
    setEdges(canvas.edges);
    setGroups(canvas.groups);
    setStickies(canvas.stickies);
    setSchedulers(canvas.schedulers);
    setUserScore(typeof o.user_score === 'number' ? o.user_score : null);
    setLlmScore(typeof o.llm_score === 'number' ? o.llm_score : null);
    if (typeof o.best_user_score === 'number') setScore(`${o.best_user_score}/12`);
    else if (typeof o.user_score === 'number') setScore(`${o.user_score}/12`);
    setDirty(false);
    getArtifacts(o.id).then((tree) => {
      const nodes = tree as OutputNode[];
      applyArtifactTree(nodes);
      if (o.run_lock?.run_id) return;
      const last = o.last_run && typeof o.last_run === 'object' ? o.last_run as { run_id?: string; status?: string } : null;
      const rid = unscoredCompletedRunId(runScoreHistoryFromArtifacts(nodes), last);
      if (!rid) return;
      setScorePrompt(true);
      getRunStatus(o.id, rid).then((live) => setLiveRun(live)).catch(() => {
        setLiveRun({
          outcome_id: o.id,
          run_id: rid,
          status: 'completed',
          phase: 'commit',
          step_index: 0,
          step_total: 0,
          started_at: '',
          elapsed_ms: 0,
          logs: [],
        });
      });
    }).catch(() => {});
  }, [pipelineName, skillMap, projectId, applyArtifactTree]);

  const loadOutcomeRef = useRef(loadOutcome);
  loadOutcomeRef.current = loadOutcome;

  useEffect(() => {
    const pid = projectIdFromSearch(loc.search);
    if (pid === projectId) return;
    setProjectId(pid);
    setOutcomeId(null);
    setOutcomeSlug(null);
    setNodes([]);
    setEdges([]);
    setGroups([]);
    setStickies([]);
    setPreviewOutcome(false);
    setPreviewSkillId(null);
    const fromNav = projectFromNavState(loc.state, pid);
    if (fromNav) {
      setPipelineName(fromNav.title || '');
      setDescription(fromNav.outcome_description || '');
      setProjectType(fromNav.outcome_type || '');
    } else {
      setPipelineName('');
      setDescription('');
      setProjectType('');
    }
    try {
      setChats(loadChats(pid, localStorage));
      setActiveChatId(localStorage.getItem(activeChatKey(pid)) || '');
      setCenterTab(migrateCenterTab(
        localStorage.getItem(studioTabKey(pid)),
        localStorage.getItem(`sh_right_tab:${pid}`),
      ));
    } catch {
      setChats([emptyChat()]);
      setActiveChatId('');
      setCenterTab('canvas');
    }
  }, [loc.search, loc.state, projectId]);

  useEffect(() => {
    const oid = new URLSearchParams(loc.search).get('outcome');
    const pid = projectIdFromSearch(loc.search);
    if (!isLoggedIn()) return;
    let cancelled = false;
    (async () => {
      if (oid) {
        try {
          const o = await getOutcome(oid);
          if (!cancelled) loadOutcomeRef.current(o);
        } catch {
          /* missing outcome stays empty */
        }
        return;
      }
      if (!pid) return;
      try {
        const p = await getProject(pid);
        if (cancelled) return;
        setPipelineName(p.title || '');
        setDescription(p.outcome_description || '');
        setProjectType(p.outcome_type || '');
        if (p.outcome_id) {
          const o = await getOutcome(p.outcome_id);
          if (!cancelled) loadOutcomeRef.current(o);
        }
      } catch {
        /* missing project stays empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loc.search]);

  useEffect(() => {
    if (!nodes.length && !groups.length && !stickies.length && !schedulers.length) return;
    try {
      writeCanvasLayout(layoutStorageKey(outcomeId, projectId), nodes, groups, stickies, localStorage, schedulers);
    } catch {
      /* ignore */
    }
  }, [nodes, groups, stickies, schedulers, outcomeId, projectId]);

  useEffect(() => {
    if (!projectId || !isLoggedIn()) {
      setProjectFiles([]);
      setProjectFilesError(null);
      return;
    }
    listProjectFiles(projectId)
      .then((rows) => {
        setProjectFiles(rows);
        setProjectFilesError(null);
      })
      .catch(() => setProjectFiles([]));
  }, [projectId]);

  useEffect(() => { document.title = `${dirty ? '*' : ''}${pipelineName} — Sourcetape`; }, [pipelineName, dirty]);

  const stopPoll = () => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => () => stopPoll(), []);

  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => {
      if (startedAtRef.current) setElapsedMs(Date.now() - startedAtRef.current);
    }, 250);
    return () => window.clearInterval(id);
  }, [running]);

  const applyLive = (live: LiveRun) => {
    setLiveRun(live);
    setElapsedMs(live.elapsed_ms);
    const started = Date.parse(live.started_at);
    if (!Number.isNaN(started)) startedAtRef.current = started;
    setNodes((ns) => applyLiveRunToNodes(ns, live.current_skill_id, live.steps));
    if (typeof live.user_score === 'number') {
      setUserScore(live.user_score);
      setScore(`${live.user_score}/12`);
    }
    if (typeof live.llm_score === 'number') setLlmScore(live.llm_score);
    if (live.review_md) setReviewMd(live.review_md);
    if (live.checks?.length || live.user_checks?.length) {
      setScoreChecks(mergeScoreChecks(live.user_checks, live.checks));
    }
  };

  const beginPoll = (oid: string, rid: string) => {
    stopPoll();
    const tick = async () => {
      try {
        const live = await getRunStatus(oid, rid);
        applyLive(live);
        if (live.plan_md) {
          try {
            applyArtifactTree((await getArtifacts(oid)) as OutputNode[]);
          } catch {
            /* ignore */
          }
        }
        if (live.status === 'completed' || live.status === 'failed') {
          stopPoll();
          setRunning(false);
          setLastRun('just now');
          try {
            applyArtifactTree((await getArtifacts(oid)) as OutputNode[]);
          } catch {
            /* ignore */
          }
          if (live.status === 'completed') {
            setScorePrompt(true);
            setCenterTab('tests');
          }
        }
      } catch {
        /* poll again */
      }
    };
    void tick();
    pollRef.current = window.setInterval(tick, 500);
  };

  const linkProjectOutcome = async (oid: string) => {
    if (!projectId) return;
    try {
      await patchProject(projectId, { outcome_id: oid });
    } catch {
      /* project link is best-effort */
    }
  };

  const ensureSaved = async () => {
    const payload = buildOutcomePayload(pipelineName, description, nodes, edges, groups, stickies, schedulers);
    if (outcomeId) {
      if (dirty) {
        const c = await updateOutcome(outcomeId, payload);
        setOutcomeId(c.id);
        setOutcomeSlug(c.slug || null);
        setDirty(false);
        await linkProjectOutcome(c.id);
        return c.id;
      }
      await linkProjectOutcome(outcomeId);
      return outcomeId;
    }
    const c = await createOutcome(payload);
    setOutcomeId(c.id);
    setOutcomeSlug(c.slug || null);
    setDirty(false);
    await linkProjectOutcome(c.id);
    return c.id;
  };

  const onSave = async () => {
    if (running) return;
    if (!isLoggedIn()) { location.assign('/login?next=' + encodeURIComponent(location.pathname + location.search)); return; }
    setSaving(true);
    try {
      await ensureSaved();
    } catch (e) { alert(e instanceof Error ? e.message : 'save failed'); } finally { setSaving(false); }
  };

  const startLive = async (starter: (id: string) => Promise<{ run_id: string }>) => {
    if (running || nodes.length === 0) return;
    if (!isLoggedIn()) { location.assign('/login?next=' + encodeURIComponent(location.pathname + location.search)); return; }
    setSelectedId(null);
    setScorePrompt(false);
    setScoreChecks([]);
    setReviewMd('');
    setArtifactFocusId(null);
    setNodes((ns) => ns.map((n) => ({ ...n, status: 'idle' as const })));
    setRunning(true);
    startedAtRef.current = Date.now();
    setElapsedMs(0);
    try {
      const id = await ensureSaved();
      const start = await starter(id);
      beginPoll(id, start.run_id);
    } catch (e) {
      setRunning(false);
      const msg = e instanceof Error ? e.message : 'run failed';
      if (msg.includes('start hosting') || msg.includes('buy credits')) {
        getAccount().then((a) => {
          setCreditBalance(a.credit_balance);
          setHostingActive(a.subscription?.status === 'active');
        }).catch(() => setCreditBalance(0));
      }
      alert(msg);
    }
  };

  const onRun = async () => {
    await startLive((id) => runOutcome(id));
  };

  const onTrain = async () => {
    if (!outcomeId || !liveRun?.run_id) return;
    if (userScore == null || userScore >= 10) return;
    if ((liveRun.epoch ?? 0) >= 5) return;
    await startLive((id) => trainOutcome(id, liveRun.run_id));
  };

  const [pendingPlaybookEdits, setPendingPlaybookEdits] = useState<{ op: string; skill_id: string; content: string; label: string }[]>([]);

  const onApplyPlaybook = async () => {
    if (!outcomeId || !pendingPlaybookEdits.length) return;
    const { applyPayload } = await import('./playbookEdits');
    const { apiJSON } = await import('../../auth/token');
    const body = applyPayload(pendingPlaybookEdits, 'reflector');
    if (!body.ops.length) return;
    try {
      await apiJSON(`/api/outcomes/${outcomeId}/playbook`, { method: 'POST', body: JSON.stringify(body) });
      setPendingPlaybookEdits([]);
      try { applyArtifactTree((await getArtifacts(outcomeId)) as OutputNode[]); } catch { /* ignore */ }
    } catch (e) {
      alert(e instanceof Error ? e.message : 'playbook apply failed');
    }
  };

  const onDismissPlaybookEdits = async () => {
    const { dismissPending } = await import('./playbookEdits');
    setPendingPlaybookEdits(dismissPending(pendingPlaybookEdits));
  };

  const onSubmitScore = async (value: number, note: string) => {
    const oid = outcomeIdRef.current;
    const rid = scoreTargetRunId(liveRunRef.current?.run_id, runScoreHistoryFromArtifacts(artifactTreeRef.current));
    if (!oid || !rid) {
      alert('No run to score. Run the pipeline first.');
      return;
    }
    try {
      const res = await scoreRun(oid, rid, value, note);
      setUserScore(res.user_score);
      setLlmScore(res.llm_score);
      setReviewMd(res.review_md || '');
      setScoreChecks(mergeScoreChecks(res.user_checks, res.checks));
      setScore(`${res.user_score}/12`);
      setScorePrompt(false);
      const { pendingEditsFromScore } = await import('./playbookEdits');
      setPendingPlaybookEdits(pendingEditsFromScore((res as { deltas?: { op?: string; skill_id?: string; content?: string }[] }).deltas));
      try {
        applyArtifactTree((await getArtifacts(oid)) as OutputNode[]);
      } catch {
        /* ignore */
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : 'score failed');
    }
  };

  const onSkipScore = () => setScorePrompt(false);

  const onReviewNote = async (note: string) => {
    if (!outcomeId || !liveRun?.run_id || !note.trim()) return;
    try {
      const res = await reviewRun(outcomeId, liveRun.run_id, note);
      setLlmScore(res.llm_score);
      setReviewMd(res.review_md || '');
      setScoreChecks(mergeScoreChecks(res.user_checks, res.checks));
      try {
        applyArtifactTree((await getArtifacts(outcomeId)) as OutputNode[]);
      } catch {
        /* ignore */
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : 'review failed');
    }
  };

  const onRunStep = async (id: string) => {
    setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, status: 'running' } : n)));
    await new Promise((r) => setTimeout(r, 600));
    setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, status: 'success' } : n)));
  };

  const onResolve = async (d: string) => {
    const r = await resolveSkill(d);
    addSkill(toPalette(r, d));
  };

  const onCreateSkill = async (input: { name: string; description: string; body: string }) => {
    const created = await createSkill(input);
    addSkill(toPalette(created, input.description));
  };

  const updateActiveChat = (updater: (c: Chat) => Chat) => {
    setChats((cs) => {
      const id = activeChatId || cs[0]?.id;
      return cs.map((c) => (c.id === id ? updater(c) : c));
    });
  };

  const onNewChat = () => {
    const agentId = listedAgentId(activeChat?.agentId, allowLocalModels());
    const chat = emptyChat(agentId);
    if (activeChat && activeChat.agentId === agentId) {
      chat.model = activeChat.model;
      chat.baseUrl = activeChat.baseUrl;
    }
    setChats((cs) => [chat, ...cs]);
    setActiveChatId(chat.id);
    setChatError(null);
  };

  const onSelectChat = (id: string) => { setActiveChatId(id); setChatError(null); };

  const onDeleteChat = (id: string) => {
    setChats((cs) => {
      const next = cs.filter((c) => c.id !== id);
      const fallback = next.length ? next : [emptyChat()];
      if (id === activeChatId) setActiveChatId(fallback[0].id);
      return fallback;
    });
  };

  const onSetChatAgent = (agentId: Agent['id']) => {
    const agent = AGENTS.find((a) => a.id === agentId);
    setModels([]);
    setLoadedModels([]);
    updateActiveChat((c) => ({
      ...c,
      agentId,
      model: c.model && c.agentId === agentId ? c.model : (llmSettings?.provider === agentId && llmSettings.model) || agent?.model || '',
    }));
  };

  const onSaveSettings = async () => {
    if (!activeChat) return;
    if (!isLoggedIn()) {
      if (activeChat.agentId === 'lmstudio') {
        setSettingsMsg('LM Studio URL and model stay with this chat in the browser. Sign in to save them to an account.');
        return;
      }
      location.assign('/login?next=' + encodeURIComponent(location.pathname + location.search));
      return;
    }
    setSavingSettings(true);
    setSettingsMsg(null);
    try {
      const saved = await putLLMSettings({
        provider: activeChat.agentId,
        model: activeChat.model,
        base_url: activeChat.baseUrl,
        openai_key: openaiKey || undefined,
        anthropic_key: anthropicKey || undefined,
      });
      setLlmSettings(saved);
      setOpenaiKey('');
      setAnthropicKey('');
      setSettingsMsg('Saved to your account.');
    } catch (e) {
      setSettingsMsg(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSavingSettings(false);
    }
  };

  const applyStudioModels = useCallback((ids: string[], loaded: string[], chatId: string) => {
    setModels(ids);
    setLoadedModels(loaded);
    if (loaded.length === 0) return;
    setChats((cs) =>
      cs.map((c) => {
        if (c.id !== chatId || c.agentId !== 'lmstudio') return c;
        if (c.model && (ids.includes(c.model) || loaded.includes(c.model))) return c;
        return { ...c, model: loaded[0] };
      }),
    );
  }, []);

  const onRefreshModels = useCallback(async () => {
    const chat = chats.find((c) => c.id === activeChatId) ?? chats[0];
    if (!chat || (chat.agentId !== 'lmstudio' && chat.agentId !== 'openrouter')) return;
    try {
      const res = chat.agentId === 'openrouter'
        ? await listLLMModels({ provider: 'openrouter' })
        : await listLLMModels({ baseUrl: chat.baseUrl });
      const ids = res.models || [];
      const loaded = res.loaded || [];
      if (chat.agentId === 'openrouter') {
        setModels(ids);
        setLoadedModels([]);
        setChats((cs) =>
          cs.map((c) => {
            if (c.id !== chat.id || c.agentId !== 'openrouter') return c;
            if (c.model) return c;
            return { ...c, model: ids[0] || c.model };
          }),
        );
        if (ids.length) {
          setSettingsMsg(`${ids.length} hosted models`);
        } else {
          setSettingsMsg('No hosted chat models');
        }
        return;
      }
      applyStudioModels(ids, loaded, chat.id);
      if (loaded.length) {
        setSettingsMsg(`${loaded.length} loaded in LM Studio`);
      } else if (ids.length) {
        setSettingsMsg(`${ids.length} models found. Load one in LM Studio, then refresh.`);
      } else {
        setSettingsMsg('No chat models in LM Studio');
      }
    } catch (e) {
      setModels([]);
      setLoadedModels([]);
      setSettingsMsg(e instanceof Error ? e.message : 'could not list models');
    }
  }, [activeChatId, chats, applyStudioModels]);

  useEffect(() => {
    if (!activeChat || activeChat.agentId !== 'lmstudio') return;
    const t = window.setTimeout(() => {
      void onRefreshModels();
    }, 350);
    return () => window.clearTimeout(t);
  }, [activeChat?.id, activeChat?.agentId, activeChat?.baseUrl]);

  useEffect(() => {
    if (!activeChat || activeChat.agentId !== 'openrouter') return;
    const t = window.setTimeout(() => {
      void onRefreshModels();
    }, 350);
    return () => window.clearTimeout(t);
  }, [activeChat?.id, activeChat?.agentId]);

  const onApplyComposeToCanvas = (recommendations: ComposeRecommendation[]) => {
    if (running) return;
    const { nodes: nextNodes, edges: nextEdges } = buildComposePipeline(recommendations, skillMap);
    if (nextNodes.length === 0) return;
    setNodes(layoutCanvasNodes(nextNodes));
    setEdges(nextEdges);
    setSelectedId(nextNodes[0].id);
    setCenterTab('canvas');
    markDirty();
  };

  const onSendChat = async (content: string) => {
    if (!activeChat || typing || running) return;
    if (activeChat.agentId === 'openrouter' && hostedCreditsMode({
      chatHosted: true,
      settingsHosted: false,
      loggedIn: isLoggedIn(),
      creditBalance,
      hostingActive,
    })) {
      setChatError(isLoggedIn()
        ? 'start hosting to use hosted models'
        : 'sign in to use hosted models; new accounts get 20 free credits');
      return;
    }
    const chatId = activeChat.id;
    const userMsg = { id: `m${Date.now()}`, role: 'user' as const, content, time: nowTime() };
    const history = [...activeChat.messages, userMsg];
    updateActiveChat((c) => ({
      ...c,
      title: c.title === 'New chat' ? content.slice(0, 42) : c.title,
      messages: history,
      updatedAt: 'now',
    }));
    setTyping(true);
    setChatError(null);
    try {
      const reply = await sendChat({
        provider: activeChat.agentId,
        model: activeChat.model || undefined,
        base_url: activeChat.agentId === 'lmstudio' ? activeChat.baseUrl : undefined,
        openai_key: openaiKey || undefined,
        anthropic_key: anthropicKey || undefined,
        context: activeChat.context,
        messages: history.map((m) => ({ role: m.role, content: m.content })),
        project: {
          id: projectId || undefined,
          title: pipelineName,
          description,
          outcome_type: projectType,
          nodes: nodes.map((n) => ({ id: n.id, skill_id: n.skillId, note: n.note, body: n.body })),
          edges: edges.map((e) => ({ from: e.from, to: e.to, field: e.field })),
        },
      });
      (reply.created_skills || []).forEach((s) => addSkill(toPalette(s)));
      setChats((cs) =>
        cs.map((c) =>
          c.id === chatId
            ? {
                ...c,
                messages: [
                  ...c.messages,
                  {
                    id: `m${Date.now()}-r`,
                    role: 'assistant' as const,
                    content: reply.content,
                    time: nowTime(),
                    recommendations: reply.recommendations,
                  },
                ],
              }
            : c,
        ),
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'chat failed';
      setChatError(msg);
      if (msg.includes('start hosting') || msg.includes('buy credits')) {
        getAccount().then((a) => {
          setCreditBalance(a.credit_balance);
          setHostingActive(a.subscription?.status === 'active');
        }).catch(() => setCreditBalance(0));
      }
    } finally {
      setTyping(false);
    }
  };

  const [analyzePlaybookOps, setAnalyzePlaybookOps] = useState<{ op: string; skill_id: string; content: string; label: string }[]>([]);
  const [playbookHistory, setPlaybookHistory] = useState<{ id: string; label: string }[]>([]);
  useEffect(() => { if (!outcomeId) return; void (async () => { try { const { apiJSON } = await import('../../auth/token'); const { historyRows } = await import('./playbookHistory'); setPlaybookHistory(historyRows((await apiJSON<{ history?: { id: string; at: string; kind: string; fork_name?: string }[] }>(`/api/outcomes/${outcomeId}/playbook`)).history || [])); } catch { /* keep last */ } })(); }, [outcomeId]);

  const onSendAnalyzeChat = async (content: string) => {
    if (!activeChat || analyzeTyping) return;
    if (activeChat.agentId === 'openrouter' && hostedCreditsMode({
      chatHosted: true,
      settingsHosted: false,
      loggedIn: isLoggedIn(),
      creditBalance,
      hostingActive,
    })) {
      setAnalyzeError(isLoggedIn()
        ? 'start hosting to use hosted models'
        : 'sign in to use hosted models; new accounts get 20 free credits');
      return;
    }
    const userMsg = { id: `a${Date.now()}`, role: 'user' as const, content, time: nowTime() };
    const history = [...analyzeMessages, userMsg];
    setAnalyzeMessages(history);
    setAnalyzeTyping(true);
    setAnalyzeError(null);
    try {
      const reply = await sendChat({
        provider: activeChat.agentId,
        model: activeChat.model || undefined,
        base_url: activeChat.agentId === 'lmstudio' ? activeChat.baseUrl : undefined,
        openai_key: openaiKey || undefined,
        anthropic_key: anthropicKey || undefined,
        context: activeChat.context,
        mode: 'analyze',
        messages: history.map((m) => ({ role: m.role, content: m.content })),
        project: {
          id: projectId || undefined,
          title: pipelineName,
          description,
          outcome_type: projectType,
          nodes: nodes.map((n) => ({ id: n.id, skill_id: n.skillId, note: n.note, body: n.body })),
          edges: edges.map((e) => ({ from: e.from, to: e.to, field: e.field })),
          runs: packAnalyzeRuns(runScoreHistoryFromArtifacts(artifactTree), {
            runId: liveRun?.run_id ?? null,
            running,
            elapsedMs,
            userScore,
            llmScore,
            reviewMd,
            checks: scoreChecks,
            model: liveRun?.model ?? '',
            promptTokens: liveRun?.prompt_tokens ?? null,
          }),
          playbooks: playbooksFromArtifacts(artifactTree),
        },
      });
      setAnalyzeMessages((ms) => [
        ...ms,
        {
          id: `a${Date.now()}-r`,
          role: 'assistant' as const,
          content: reply.content,
          time: nowTime(),
          recommendations: reply.recommendations,
        },
      ]);
      const { cardsFromAnalyzeOps } = await import('./playbookHistory');
      setAnalyzePlaybookOps(cardsFromAnalyzeOps((reply as { playbook_ops?: { op?: string; skill_id?: string; content?: string }[] }).playbook_ops));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'chat failed';
      setAnalyzeError(msg);
      if (msg.includes('start hosting') || msg.includes('buy credits')) {
        getAccount().then((a) => {
          setCreditBalance(a.credit_balance);
          setHostingActive(a.subscription?.status === 'active');
        }).catch(() => setCreditBalance(0));
      }
    } finally {
      setAnalyzeTyping(false);
    }
  };

  const pbWrite = async (suffix: string, body: unknown, clearOps = false) => {
    if (!outcomeId) return;
    const { apiJSON } = await import('../../auth/token');
    await apiJSON(`/api/outcomes/${outcomeId}/playbook${suffix}`, { method: 'POST', body: JSON.stringify(body) });
    if (clearOps) setAnalyzePlaybookOps([]);
    try { applyArtifactTree((await getArtifacts(outcomeId)) as OutputNode[]); } catch { /* ignore */ }
    try { const { historyRows } = await import('./playbookHistory'); setPlaybookHistory(historyRows((await apiJSON<{ history?: { id: string; at: string; kind: string; fork_name?: string }[] }>(`/api/outcomes/${outcomeId}/playbook`)).history || [])); } catch { /* keep last */ }
  };
  const onApplyAnalyzePlaybook = async () => {
    const { applyPayload } = await import('./playbookEdits');
    const body = applyPayload(analyzePlaybookOps, 'analyze');
    if (!body.ops.length) return;
    try { await pbWrite('', body, true); } catch (e) { alert(e instanceof Error ? e.message : 'playbook apply failed'); }
  };
  const onForkPlaybook = async (name: string) => {
    const { forkPayload } = await import('./playbookHistory');
    const body = forkPayload(name);
    if (!body) return;
    try { await pbWrite('/fork', body); } catch (e) { alert(e instanceof Error ? e.message : 'fork failed'); }
  };
  const onRestorePlaybook = async (entryId: string) => {
    const { restorePayload } = await import('./playbookHistory');
    const body = restorePayload(entryId);
    if (!body) return;
    try { await pbWrite('/restore', body); } catch (e) { alert(e instanceof Error ? e.message : 'restore failed'); }
  };

  const onNodeMove = (id: string, x: number, y: number) => { setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, x, y } : n))); markDirty(); };
  const onDropSkill = (skillId: string, x: number, y: number) => { const id = `n${Date.now()}`; setNodes((ns) => [...ns, { id, skillId, x, y, status: 'idle', note: '', body: skillMap[skillId]?.body ?? '' }]); setSelectedId(id); markDirty(); };
  const onRemove = (id: string) => { setNodes((ns) => ns.filter((n) => n.id !== id)); setEdges((es) => es.filter((e) => e.from !== id && e.to !== id)); setSelectedId(null); markDirty(); };
  const onUpdateNote = (id: string, note: string) => { setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, note } : n))); markDirty(); };
  const onUpdateBody = (id: string, body: string) => { setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, body } : n))); markDirty(); };
  const onEditNode = (id: string) => { setSelectedId(id); setCenterTab('content'); };
  const onPreviewSkill = (skillId: string | null) => {
    setPreviewOutcome(false);
    setPreviewSkillId((cur) => (skillId === null || skillId === cur ? null : skillId));
  };
  const onViewPrompt = (open?: boolean) => {
    setPreviewSkillId(null);
    setPreviewOutcome((cur) => (open === false ? false : open === true ? true : !cur));
    setSelectedId(PROMPT_NODE_ID);
    setCenterTab('canvas');
  };
  const onDropGroup = (type: GroupType, x: number, y: number) => { const id = `g${Date.now()}`; const w = type === 'loop' ? 280 : 340; const h = type === 'loop' ? 170 : 220; setGroups((gs) => [...gs, { id, type, x, y, w, h, title: type === 'loop' ? 'Loop' : 'Process', status: 'idle', iterations: 3 }]); markDirty(); };
  const onMoveGroup = (id: string, nx: number, ny: number) => { const g = groups.find((x) => x.id === id); if (!g) return; const dx = nx - g.x; const dy = ny - g.y; const memberIds = new Set(nodes.filter((n) => nodeInGroup(n, g)).map((n) => n.id)); setNodes((ns) => ns.map((n) => (memberIds.has(n.id) ? { ...n, x: n.x + dx, y: n.y + dy } : n))); setGroups((gs) => gs.map((x) => (x.id === id ? { ...x, x: nx, y: ny } : x))); markDirty(); };
  const onUpdateGroupTitle = (id: string, title: string) => { setGroups((gs) => gs.map((g) => (g.id === id ? { ...g, title } : g))); markDirty(); };
  const onUpdateGroupIterations = (id: string, n: number) => { setGroups((gs) => gs.map((g) => (g.id === id ? { ...g, iterations: n } : g))); markDirty(); };
  const onRemoveGroup = (id: string) => { setGroups((gs) => gs.filter((g) => g.id !== id)); markDirty(); };
  const onDropSticky = (x: number, y: number) => { const id = `s${Date.now()}`; setStickies((ss) => [...ss, { id, x, y, w: 200, h: 140, text: '' }]); markDirty(); };
  const onMoveSticky = (id: string, x: number, y: number) => { setStickies((ss) => ss.map((s) => (s.id === id ? { ...s, x, y } : s))); markDirty(); };
  const onUpdateSticky = (id: string, text: string) => { setStickies((ss) => ss.map((s) => (s.id === id ? { ...s, text } : s))); markDirty(); };
  const onRemoveSticky = (id: string) => { setStickies((ss) => ss.filter((s) => s.id !== id)); markDirty(); };
  const onDropScheduler = (x: number, y: number) => {
    const id = `sch${Date.now()}`;
    setSchedulers((ss) => [...ss, { id, x, y, title: 'Listen', enabled: false, cadence: '@every 6h', action: { kind: 'search' } }]);
    markDirty();
  };
  const onMoveScheduler = (id: string, x: number, y: number) => { setSchedulers((ss) => ss.map((s) => (s.id === id ? { ...s, x, y } : s))); markDirty(); };
  const onUpdateScheduler = (id: string, patch: Partial<Scheduler>) => { setSchedulers((ss) => ss.map((s) => (s.id === id ? { ...s, ...patch } : s))); markDirty(); };
  const onRemoveScheduler = (id: string) => { setSchedulers((ss) => ss.filter((s) => s.id !== id)); markDirty(); };

  const scoreHistory = useMemo(() => runScoreHistoryFromArtifacts(artifactTree), [artifactTree]);
  const bestRecorded = bestUserScore(scoreHistory);
  const canTrain = canTrainFromScores({
    userScore,
    epoch: liveRun?.epoch ?? 0,
    running,
    success: liveRun?.success,
    bestRecorded,
  });
  const displayScore = bestRecorded != null ? `${bestRecorded}/12` : score;
  const chatHosted = activeChat?.agentId === 'openrouter';
  const settingsHosted = llmSettings?.provider === 'openrouter';
  const hostedCredits = hostedCreditsMode({
    chatHosted: !!chatHosted,
    settingsHosted: !!settingsHosted,
    loggedIn: isLoggedIn(),
    creditBalance,
    hostingActive,
  });
  const chatHostedCredits = hostedCreditsMode({
    chatHosted: !!chatHosted,
    settingsHosted: false,
    loggedIn: isLoggedIn(),
    creditBalance,
    hostingActive,
  });

  return {
    skills, skillMap, pipelineName, setPipelineName: (v: string) => { setPipelineName(v.replace(/\*$/, '')); markDirty(); }, description, setDescription: (v: string) => { setDescription(v); markDirty(); }, version, dirty, nodes, edges, groups, stickies, schedulers, selectedId, setSelectedId, centerTab, setCenterTab, rightTab, setRightTab, chatDocked, setChatDocked, chatVisible, running, saving, activeEdgeIds, lastRun, score: displayScore, showExport, setShowExport, showFork, setShowFork, selectedNode, previewSkill, previewOutcome,
    onNodeMove, onDropSkill, onRemove, onUpdateNote, onUpdateBody, onEditNode, onPreviewSkill, onViewPrompt, onDropGroup, onMoveGroup, onUpdateGroupTitle, onUpdateGroupIterations, onRemoveGroup, onDropSticky, onMoveSticky, onUpdateSticky, onRemoveSticky, onDropScheduler, onMoveScheduler, onUpdateScheduler, onRemoveScheduler, onRunStep, onRun, onSave, onResolve, onExport: () => { if (!running) setShowExport(true); }, onFork: () => { if (!running) setShowFork(true); },
    artifactTree, liveRun, elapsedMs, scorePrompt, userScore, llmScore, reviewMd, scoreChecks, scoreHistory,
    testArtifacts: artifactsForRun(artifactTree, liveRun?.run_id),
    artifactFocusId,
    onOpenArtifact: (id: string) => {
      const files = artifactsForRun(artifactTree, id);
      setArtifactFocusId(files[0]?.id || id);
      setCenterTab('artifacts');
    },
    canTrain,
    onTrain, onSubmitScore, onSkipScore, onReviewNote, pendingPlaybookEdits, onApplyPlaybook, onDismissPlaybookEdits,
    chats, activeChat, activeChatId, typing, chatError, llmSettings, models, loadedModels, savingSettings, settingsMsg, openaiKey, anthropicKey,
    hostedCredits, chatHostedCredits,
    analyzeMessages, analyzeTyping, analyzeError, onSendAnalyzeChat, analyzePlaybookOps, playbookHistory, onApplyAnalyzePlaybook, onForkPlaybook, onRestorePlaybook,
    onNewChat, onSelectChat, onDeleteChat, onSetChatAgent, onSendChat, onApplyComposeToCanvas, onCreateSkill, onSaveSettings, onRefreshModels,
    onSetChatModel,
    onSetChatBaseUrl: (baseUrl: string) => updateActiveChat((c) => ({ ...c, baseUrl })),
    onSetChatContext: (context: string) => updateActiveChat((c) => ({ ...c, context })),
    onSetOpenAIKey: setOpenaiKey,
    onSetAnthropicKey: setAnthropicKey,
    projectId, outcomeId, outcomeSlug, projectFiles, projectFilesBusy, projectFilesError,
    onAddProjectFiles: async (files: File[]) => {
      if (!projectId || !files.length) return;
      setProjectFilesBusy(true);
      setProjectFilesError(null);
      try {
        for (const file of files) {
          const created = await uploadProjectFile(projectId, file);
          setProjectFiles((cur) => [...cur, created]);
        }
      } catch (e) {
        setProjectFilesError(e instanceof Error ? e.message : 'upload failed');
      } finally {
        setProjectFilesBusy(false);
      }
    },
    onRemoveProjectFile: async (id: string) => {
      if (!projectId) return;
      setProjectFilesError(null);
      try {
        const res = await deleteProjectFile(projectId, id);
        if (!res.ok && res.status !== 204) throw new Error('could not remove file');
        setProjectFiles((cur) => cur.filter((f) => f.id !== id));
      } catch (e) {
        setProjectFilesError(e instanceof Error ? e.message : 'remove failed');
      }
    },
  };
}
